import { clsx } from 'clsx';

export interface PaginationProps {
  page: number;
  total: number;
  onChange: (p: number) => void;
  siblings?: number;
  className?: string;
}

function range(from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = from; i <= to; i++) out.push(i);
  return out;
}

function build(page: number, total: number, siblings: number): (number | 'gap')[] {
  if (total <= 7 + siblings * 2) return range(1, total);
  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, total);
  const out: (number | 'gap')[] = [1];
  if (left > 2) out.push('gap');
  out.push(...range(Math.max(left, 2), Math.min(right, total - 1)));
  if (right < total - 1) out.push('gap');
  out.push(total);
  return out;
}

const BASE = 'min-w-[28px] h-7 px-1.5 inline-flex items-center justify-center rounded-md text-xs font-semibold border transition-colors';

export function Pagination({ page, total, onChange, siblings = 1, className }: PaginationProps) {
  if (total <= 1) return null;
  const items = build(page, total, siblings);

  return (
    <nav className={clsx('inline-flex items-center gap-1', className)} aria-label="Pagination">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className={clsx(BASE, 'bg-transparent border-line text-fg-secondary hover:border-line disabled:opacity-40 disabled:cursor-not-allowed')}
        aria-label="Предыдущая страница"
      >
        ‹
      </button>
      {items.map((it, i) =>
        it === 'gap' ? (
          <span key={`g${i}`} className="text-fg-muted px-1">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => onChange(it)}
            className={clsx(
              BASE,
              it === page
                ? 'bg-accent-bg border-accent-border text-accent'
                : 'bg-transparent border-line text-fg-secondary hover:border-line',
            )}
            aria-current={it === page ? 'page' : undefined}
          >
            {it}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page >= total}
        onClick={() => onChange(page + 1)}
        className={clsx(BASE, 'bg-transparent border-line text-fg-secondary hover:border-line disabled:opacity-40 disabled:cursor-not-allowed')}
        aria-label="Следующая страница"
      >
        ›
      </button>
    </nav>
  );
}
